import React from "react";
import ReactApexChart from "react-apexcharts";
import { CARD_TITLE } from "../utils/styles";

const CallStatusDistributionChart = ({ agent, stats }) => {
  const total = stats?.totalCall || 0;

  const { series, options, legendItems, isNoData, getPercentage } = React.useMemo(() => {
    if (!stats) {
      return {
        series: [],
        options: {},
        legendItems: [],
        isNoData: true,
        getPercentage: () => "0.00%",
      };
    }
    const isInquery = agent === "inquery";
    const cConcluded = stats.callConcluded || 0;
    const cInterested = stats.interestedInFuture || 0;
    const cNotInterested = stats.notInterested || 0;
    const cNotConcluded = stats.notConcluded || 0;
    const cFail = stats.callFail || 0;

    const total = stats.totalCall || 0;

    const getPercentage = (val) => {
      if (!total) return "0.00%";
      return `${((val / total) * 100).toFixed(2)}%`;
    };

    const normalSeries = [cConcluded, cNotConcluded, cInterested, cNotInterested, cFail];
    let chartLabels = [];
    let chartColors = [];

    chartLabels = ["Call Concluded", "Not Concluded", "Interested in Future", "Not Interested", "Failed Calls"];
    chartColors = ["#10b981", "#3b82f6", "#ff9f1c", "#a855f7", "#ef4444"];

    const isNoData = total === 0 || normalSeries.reduce((a, b) => a + b, 0) === 0;

    const series = isNoData ? [1] : normalSeries;

    const finalLabels = isNoData ? ["No Data Available"] : chartLabels;
    const finalColors = isNoData ? ["#E4E6EF"] : chartColors;

    const centerLabel = isNoData ? "Total Calls" : "Total Calls";

    const options = {
      chart: {
        type: "donut",
        height: 220,
      },
      colors: finalColors,
      stroke: {
        show: true,
        width: 2,
        colors: ["#ffffff"],
      },
      labels: finalLabels,
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          donut: {
            size: "68%",
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "13px",
                fontWeight: 500,
                color: "#7e8299",
                offsetY: -8,
              },
              value: {
                show: true,
                fontSize: "25px",
                fontWeight: 800,
                color: "#181c32",
                offsetY: 4,
                formatter: (val) => isNoData ? "0" : Number(val).toLocaleString(),
              },
              total: {
                show: true,
                label: centerLabel,
                fontSize: "13px",
                fontWeight: 500,
                color: "#7e8299",
                formatter: () => isNoData ? "0" : total.toLocaleString(),
              },
            },
          },
        },
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: (val) => isNoData ? "0 (100%)" : `${val.toLocaleString()} (${getPercentage(val)})`,
        },
      },
    };

    const legendItems = isNoData
      ? chartLabels.map((lbl) => ({
        label: lbl,
        value: 0,
        percentage: "0.00%",
        color: "#E4E6EF"
      }))
      : chartLabels.map((lbl, idx) => ({
        label: lbl,
        value: normalSeries[idx],
        percentage: getPercentage(normalSeries[idx]),
        color: chartColors[idx]
      }));

    return { series, options, legendItems, isNoData, getPercentage };
  }, [agent, stats]);

  if (!stats) return null;

  return (
    <div className="card card-custom h-100 shadow-sm border-0 bg-white rounded-lg p-5" style={{ padding: "20px 24px" }}>
      <div className="card-header border-0 p-0 mb-5 d-flex justify-content-between align-items-center">
        <h3
          className="card-title font-weight-bolder text-dark font-size-h5 m-0"
          style={CARD_TITLE}
        >
          Call Status Distribution
        </h3>
      </div>
      <div className="card-body p-0 d-flex align-items-center justify-content-center flex-wrap" style={{ minHeight: "220px", gap: "28px" }}>
        {/* Donut Chart container */}
        <div
          className="d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: "220px", height: "220px" }}
        >
          <ReactApexChart key={total} options={options} series={series} type="donut" width="100%" height={220} />
        </div>

        {/* Custom Legend Container */}
        <div
          className="py-1 d-flex flex-column justify-content-center"
          style={{ flex: "1 1 310px", maxWidth: "340px", minWidth: "260px" }}
        >
          {legendItems.map((item, idx) => (
            <div
              key={idx}
              className="d-flex align-items-center justify-content-between px-2 py-1 mb-1 rounded-lg"
              style={{
                borderBottom: "1px dashed #F3F4F6",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                if (!isNoData) {
                  e.currentTarget.style.backgroundColor = "rgba(110, 66, 193, 0.03)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isNoData) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              <div className="d-flex align-items-center">
                <div
                  style={{
                    width: "13px",
                    height: "13px",
                    backgroundColor: item.color,
                    borderRadius: "3px",
                    flexShrink: 0,
                  }}
                />
                <span
                  className="ml-4 text-dark-75"
                  style={{ fontSize: "0.86rem", fontWeight: "600", color: isNoData ? "#B5B5C3" : "#3F4254" }}
                >
                  {item.label}
                </span>
              </div>
              {/* Fixed-width metrics */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "55px 70px",
                  alignItems: "center",
                  width: "125px",
                  flexShrink: 0,
                }}
              >
                <span
                  className="text-dark"
                  style={{
                    fontSize: "0.88rem",
                    fontWeight: "700",
                    color: isNoData ? "#B5B5C3" : "#181C32",
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {item.value.toLocaleString()}
                </span>
                <span
                  className="text-muted"
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: "500",
                    color: isNoData ? "#D1D5DB" : "#7E8299",
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  ({item.percentage})
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(CallStatusDistributionChart);
